import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { FileUploader } from "react-drag-drop-files";
import { PrimaryButton } from "../../elements/buttons";
import useUploadMedia from "../../../hooks/useUploadMedia";
import useCreateUpdateCertificate from "../../../hooks/certificates/useCreateUpdateCertificate";
import useGetCertificate from "../../../hooks/certificates/useGetCertificate";
import Spinner from '../../spinner';
import { toast } from "react-hot-toast";

const fileTypes = ["JPG", "PNG", "JPEG", "WEBP"];

const AddEditCertificateForm = ({ isEdit = false }) => {
  const { certificateId } = useParams();
  const navigate = useNavigate();

  const { control, setValue, handleSubmit, reset, getValues, formState: { errors } } = useForm({
    defaultValues: {
      name: "",
      media_id: "",
    }
  });

  const [imageUploading, setImageUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [imageId, setImageId] = useState("");
  const [imageError, setImageError] = useState("");

  // For editing, fetch certificate details
  useGetCertificate({
    certificateId: isEdit ? certificateId : null,
    onSuccess: (data) => {
      if (isEdit) {
        const resData = data?.data || {};
        setValue("name", resData.name || "");
        if (resData.media_id) {
          setImageId(resData.media_id);
          setImagePreview(resData.media_path || "");
          setValue("media_id", resData.media_id);
        }
      }
    }
  });

  // Image upload handlers
  const handleMediaUploadSuccess = (data) => {
    setImageId(data.data.image_id);
    setImagePreview(data.data.image_url);
    setValue("media_id", data.data.image_id);
    setImageUploading(false);
    toast.success("Image uploaded successfully", {
      position: 'top-right',
      duration: 2000
    });
  };

  const handleMediaUploadError = (error) => {
    setImageUploading(false);
    toast.error(error?.response?.data?.message || "Error uploading image", {
      position: 'top-right',
      duration: 3000
    });
  };

  const { upload } = useUploadMedia({
    url: 'upload/image',
    onSuccess: handleMediaUploadSuccess,
    onError: handleMediaUploadError
  });

  const handleImageUpload = (file) => {
    setImageUploading(true);
    upload.mutate({ thumbnail: file, screen: "CERTIFICATE" });
  };

  const handleReUpload = () => {
    setImagePreview("");
    setImageId("");
    setValue("media_id", "");
  };

  const { handleSubmit: handleCreateUpdate } = useCreateUpdateCertificate({
    reset: () => reset()
  });

  const onSubmit = (formData) => {
    if (!imageId) {
      setImageError("Certificate image is required");
      return;
    }
    setImageError("");
    const payload = {
      certificate_id: isEdit ? certificateId : "",
      name: formData.name,
      media_id: imageId,
    };
    handleCreateUpdate(payload);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="py-4">
      <div className="mb-6">
        <Controller
          name="name"
          control={control}
          rules={{
            required: "Certificate Name is required",
          }}
          render={({ field: { value, onChange } }) => (
            <div>
              <label htmlFor="Certificate Name" className='label'>Certificate Name <small className="text-red-700">*</small></label>
              <input 
                required 
                type="text" 
                placeholder='Enter Certificate Name' 
                name='name'
                className='input' 
                value={value} 
                onChange={onChange} 
              />
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>
              )}
            </div>
          )}
        />
      </div>

      <div className="mb-6">
        <label htmlFor="Certificate Image" className='label'>Image <span className="text-red-700">*</span></label>
        <div className='w-full mt-2 flex flex-col items-center'>
          {imageUploading ? (
            <div className="flex flex-col items-center justify-center h-64 w-full">
              <Spinner />
              <p className="mt-2">Uploading image...</p>
            </div>
          ) : !imagePreview ? (
            <FileUploader
              name="certificate_image"
              types={fileTypes}
              handleChange={handleImageUpload}
              classes="file-uploader"
              hoverTitle='Drop here'
            />
          ) : (
            <div className='border border-dashed border-blue-700 rounded flex flex-col items-center justify-center w-full p-2'>
              <div className="relative w-full flex items-center justify-center" style={{ minHeight: "240px" }}>
                <img
                  src={imagePreview}
                  alt="certificate"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "400px",
                    objectFit: "contain",
                    width: "auto",
                    height: "auto",
                    display: "block"
                  }}
                  className="rounded bg-white shadow"
                />
              </div>
              <div className="w-full mt-2 flex flex-col items-center">
                <button
                  type="button"
                  className="mt-2 text-blue-600 underline text-xs"
                  onClick={handleReUpload}
                >
                  Re-Upload
                </button>
              </div>
            </div>
          )}
          {imageError && (
            <p className="text-red-500 text-xs mt-2">{imageError}</p>
          )}
        </div>
      </div>

      <div className="flex justify-end mt-8">
        <PrimaryButton label={isEdit ? "Update" : "Save"} type={"submit"} />
      </div>
    </form>
  );
};

export default AddEditCertificateForm;
